/**
 * @file unrecordedCoveredShare.ts
 * @description One phrasing, used by every surface that shows a wage figure
 * derived from loans, for "some of the loans here have no recorded covered
 * share, so the wage figure beside this note is incomplete".
 *
 * The KPI bar, the financial bar, the Actions Summary and the Live Impact
 * panel each render a wage delta derived from the same actions. When a loan
 * carries no usable `coveredSharePercent` its relief is excluded rather than
 * guessed at 100% or taken as 0 (SE-7422, SE-7411), which makes the number
 * they show incomplete — and a number that is quietly incomplete is exactly
 * the failure the covered-share field was added to prevent. The disclosure
 * therefore travels with the figure, and it is worded once here so the four
 * surfaces cannot drift into four different sentences.
 *
 * PROVENANCE NO LONGER CHANGES THE WORDING (SE-7622). This module used to take
 * an `effect` discriminator because the two possible authors of the figure did
 * different things with the unknown relief: this client excluded it, while the
 * server's `scenarioImpact` fell back to the whole wage
 * (`coveredWage(...) ?? usableWageOrZero(details.wage)`, backend SE-7419).
 * Backend **SE-7452** (backend-v2 PR #3223) replaced that fallback with `?? 0`,
 * so a LOAN_OUT with no usable share now relieves nothing on the server too.
 * Both authors exclude, one sentence is true of both, and the discriminator
 * was deleted rather than left as an unreachable branch — a variant that can
 * no longer occur is a trap that outlives the reader who could disprove it.
 * Restoring a "counted at the full wage" wording would re-describe a server
 * behaviour that no longer exists, which is the defect SE-7622 fixed.
 *
 * WHAT THE SENTENCE DOES NOT CLAIM. It reports what happened to the RELIEF —
 * that it was left out — and deliberately does not explain WHY the share was
 * unusable, because the surfaces do not agree on that and the server payload
 * cannot settle it. `scenarioImpact` carries no per-loan share and no count of
 * share-less loans, so nothing on the wire distinguishes an ABSENT share from
 * an OUT-OF-RANGE one; SE-7452 relieves nothing for either. The count is
 * therefore derived client-side from the persisted action details, and the two
 * derivations differ on purpose: the compliance panel counts strictly `null`
 * shares (`hasUnrecordedLoanShare`, SE-7411), while the SE-7422 display
 * surfaces count every loan whose relief came back `null` from
 * `loanWageRelief` — absent OR out of range — which is exactly the set the
 * server now excludes. An out-of-range share is consequently described by the
 * display surfaces as "without a recorded covered share" when a value was in
 * fact recorded, just not a possible one. That imprecision predates SE-7622,
 * belongs to the counting rule rather than to this sentence, and is left to
 * whoever reconciles the two predicates; it is noted here so the next reader
 * does not mistake it for something this file decided.
 *
 * It lives in the compliance DOMAIN rather than in `squad-planning/utils`
 * because it is the copy counterpart of `loanWageRelief` returning `null`, and
 * because the Live Impact panel is a compliance surface. A formatter parked in
 * squad-planning would have forced the compliance seam to import upwards into
 * the feature whose storage it is meant not to know about (SE-7411).
 * @module features/financial-compliance/domain/unrecordedCoveredShare
 */

/**
 * What every surface says the figure beside the note did with the unknown
 * relief: nothing. True of the client's own arithmetic (SE-7411, SE-7422) and,
 * since backend SE-7452, of the server's `scenarioImpact` as well.
 */
const EFFECT_CLAUSE = "wage relief not counted";

/**
 * Renders the "no recorded covered share" disclosure for a scenario.
 * @param loansWithoutCoveredShare - How many loans carry no usable share.
 * @returns The note, or null when every loan has a recorded share.
 */
export const formatUnrecordedCoveredShareNote = (
  loansWithoutCoveredShare: number
): string | null => {
  if (
    !Number.isFinite(loansWithoutCoveredShare) ||
    loansWithoutCoveredShare < 1
  )
    return null;
  const count = Math.trunc(loansWithoutCoveredShare);
  const noun = count === 1 ? "loan" : "loans";
  return `${String(count)} ${noun} without a recorded covered share — ${EFFECT_CLAUSE}`;
};
